import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import { guCategories } from "@/mocks/categories";
import { guEvents } from "@/mocks/events";

type SortKey = "heat" | "latest";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const initialQuery = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [sort, setSort] = useState<SortKey>("heat");

  const list = useMemo(() => {
    let result = activeCategory === "all" ? guEvents : guEvents.filter((e) => e.category === activeCategory);
    const kw = keyword.trim();
    if (kw) {
      result = result.filter(
        (e) =>
          e.title.includes(kw) ||
          e.summary.includes(kw) ||
          e.tags.some((tag) => tag.includes(kw)),
      );
    }
    return [...result].sort((a, b) =>
      sort === "heat" ? b.heat - a.heat : b.publishedAt.localeCompare(a.publishedAt),
    );
  }, [activeCategory, keyword, sort]);

  const handleCategory = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setSearchParams(next);
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://2ndhair.com.cn/" },
      { "@type": "ListItem", position: 2, name: "热门专题", item: "https://2ndhair.com.cn/topics" },
    ],
  };

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "热门专题 - 网红吃瓜事件聚合",
    description: "按类型浏览网红吃瓜事件，查看每场事件的评论区风向与网友热议。",
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: list.length,
      itemListElement: list.map((event, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: event.title,
        url: `https://2ndhair.com.cn/event/${event.id}`,
      })),
    },
  };

  return (
    <Layout>
      <Seo
        title="热门专题 - 网红吃瓜事件与评论区风向列表 | 瓜田观察"
        description="按类型浏览近期最热的网红吃瓜事件，支持关键词搜索与热度排序，每条专题都附带评论区风向、神评翻车与吃瓜群众反应记录。"
        keywords="网红吃瓜专题,吃瓜事件列表,评论区风向,网友热议,吃瓜群众"
        path="/topics"
        jsonLd={[breadcrumbJsonLd, collectionJsonLd]}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "热门专题" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            热门专题
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里是瓜田的全部专题库。每一场事件都经过评论区风向统计，标注了支持、质疑与围观三方的比例，你可以按类型筛选，也可以直接搜索关键词找你想看的那一口瓜。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
                <i className="ri-search-line text-lg text-foreground-400" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件、网红或者关键词，例如「价格」「复出」"
                  className="w-full bg-transparent py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-xs font-medium text-foreground-500">
                  排序
                </span>
                <div className="flex gap-1 rounded-full bg-background-200 p-1">
                  {[
                    { key: "heat" as SortKey, label: "按热度" },
                    { key: "latest" as SortKey, label: "按时间" },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                        sort === item.key
                          ? "bg-foreground-950 text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {guCategories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => handleCategory(cat.key)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                    activeCategory === cat.key
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-bold text-foreground-950">{list.length}</span> 场专题
            </p>
          </div>

          {list.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-ghost-2-line text-4xl text-foreground-400" />
              <p className="mt-3 text-sm text-foreground-600">
                没有找到相关的瓜，换个关键词或者切换分类试试。
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}